/*
  La Sura - order cutoff in the utility bar (sections/lasura-utility-bar.liquid).

  Before the cutoff hour it counts down to it ("Order in the next 3h 12m to make ..."); from the
  cutoff until midnight it shows the "after" line; at midnight the countdown starts again.
  Always London time, whatever the visitor's own clock says, because the kitchen is in SE4.

  The text changes quietly: no live region, so a screen reader is not interrupted every
  minute. The line the server rendered stays if anything here fails.

  Also in the mobile menu's delivery card (snippets/lasura-menu-ways.liquid, 2026-10-13). The
  menu is re-rendered about a second after load, which puts the server's line back while this
  element lives on; so when the text changes under it, it counts again at once.
*/
import { londonNow } from '@theme/lasura-kitchen-dates';

/* Seconds since midnight in London. */
const londonSeconds = () => londonNow().seconds;

/* "3h 12m" over an hour, "34 minutes" under. The last minute reads "1 minute", no seconds. */
const format = (left) => {
  const h = Math.floor(left / 3600);
  const m = Math.floor((left % 3600) / 60);
  if (h > 0) return `${h}h ${m}m`;
  const mins = Math.max(m, 1);
  return `${mins} ${mins === 1 ? 'minute' : 'minutes'}`;
};

class LasuraCutoff extends HTMLElement {
  connectedCallback() {
    this.hour = Number(this.dataset.hour);
    if (!this.querySelector('.lasura-utility__msg') || !Number.isFinite(this.hour)) return;
    this.watch = new MutationObserver(() => {
      if (this.querySelector('.lasura-utility__msg')?.textContent !== this.shown) this.tick();
    });
    this.watch.observe(this, { subtree: true, childList: true, characterData: true });
    this.tick();
  }

  disconnectedCallback() {
    clearTimeout(this.timer);
    this.watch?.disconnect();
  }

  tick() {
    clearTimeout(this.timer);
    /* Looked up each time: a re-render can swap the element. */
    this.msg = this.querySelector('.lasura-utility__msg');
    if (!this.msg) return;
    const left = this.hour * 3600 - londonSeconds();
    let text;
    let next;
    if (left > 0) {
      text = [this.dataset.before, format(left), this.dataset.beforeEnd].filter(Boolean).join(' ');
      /* On the next minute boundary. */
      next = ((left % 60) || 60) * 1000;
    } else {
      text = this.dataset.after || '';
      /* Check again at midnight (London), or within the hour if the clock changes. */
      next = Math.min(86400 - londonSeconds(), 3600) * 1000;
    }
    if (text) {
      this.shown = text;
      if (this.msg.textContent !== text) this.msg.textContent = text;
    }
    this.timer = setTimeout(() => this.tick(), Math.max(next, 1000));
  }
}

if (!customElements.get('lasura-cutoff')) customElements.define('lasura-cutoff', LasuraCutoff);
